import md5 from 'crypto-js/md5';
import { DateTime } from 'luxon';
import { map, Observable, startWith } from 'rxjs';

/* eslint-disable @typescript-eslint/no-explicit-any */
import { SelectionModel } from '@angular/cdk/collections';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { DatePipe } from '@angular/common';
import {
    AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter,
    Input, OnInit, Output, ViewChild, ViewEncapsulation
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { FiltersType, GridifyConditionEnum, GridifyLogicalEnum } from '@cvs/gridify';
import { ExcelService } from '@cvs/services/excel.service';
import { camelize, deepMerge, InternalStore, nil } from '@cvs/utils';
import { Guid } from '@cvs/utils/guid';
import { FormGroup } from '@ngneat/reactive-forms';
import { TranslocoService } from '@ngneat/transloco';

import { CvsGridService } from './cvs-grid.service';
import {
    CvsAction, CvsCellView, CvsColumnAlign, CvsDataType, CvsFilterType, CvsGridColumn, CvsGridEvent,
    CvsGridOptions, CvsGridSearchDataType, CvsGridSearchEvent, DEFAULT_GRID_OPTIONS,
    DEFAULT_GRID_OPTIONS_NOTHING, downloadFileTypes, GridData, GridPagedData, GridPagination,
    LOADING_GRID_DATA
} from './cvs-grid.types';

let nextUniqueId = 0;
@Component({
    selector: 'cvs-grid',
    templateUrl: './cvs-grid.component.html',
    exportAs: 'cvsGrid',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./cvs-grid.component1.scss']
})
export class CvsGridComponent<TData = any, TColDef extends CvsGridColumn<TData> = CvsGridColumn<any>>
    implements AfterViewInit, OnInit
{
    private readonly store = new InternalStore({} as CvsGridEvent);

    private _columns: TColDef[] | null | undefined = undefined;
    private _initialized = false;
    private _options: CvsGridOptions = DEFAULT_GRID_OPTIONS_NOTHING;
    private _showAdvancedSearchPanel = false;
    private previousPageIndex = 1;
    private storageName: string;
    private trackByKeyName = 'id';

    @Input() public border: 'default' | 'default-boxed' | 'bordered' | 'borderless' | 'bordered-default' =
        'default-boxed';
    @Input() public dataSource:
        | Observable<GridData<TData> | GridPagedData<TData> | null | undefined>
        | null
        | undefined = undefined;
    /** A unique id for the checkbox input. If none is supplied, it will be auto-generated. */
    @Input() public id: string;
    @Input() public isLoading = false;
    @Input() public newClick?: (ev: MouseEvent) => any = null;
    @Input() public rowSelected: any[] = [];
    @Input() public searchNoOfCols = 3;
    @Input() public shadow: 'none' | 'md' | 'xl' | 'sm' | 'lg' | '2xl' = 'none';
    @Output() public gridEvents: EventEmitter<CvsGridEvent> = new EventEmitter();
    @Output() public rowClick = new EventEmitter<any>();
    @Output() public rowSelectionChange = new EventEmitter<any[]>();
    @ViewChild('tableContainer') public tableContainer!: ElementRef<HTMLDivElement>;

    public advSearchFormGroup = new FormGroup({});
    public advanceSearchTempFilters: CvsFilterType[] = [];
    public cardGridCols = ' grid-cols-2 md:grid-cols-4 xl:grid-cols-8';
    public displayedColumns: string[];
    public enableSorting = false;
    public pagination = true;
    public rowSelection: SelectionModel<any> = new SelectionModel<any>(true, []);
    public savedSearchItems: Observable<CvsGridSearchDataType[]>;
    public searchInputCtrl = new FormControl('');
    public searchInputPlaceHolderText = '';
    public showAdvancedSearchSaveInput = false;
    public showColumnSelection = false;
    public toggleColumnTrigger: any;

    /**
     * Constructor
     */
    constructor(
        private router: Router,
        private excelService: ExcelService,
        private translator: TranslocoService,
        private gridService: CvsGridService,
        private cdr: ChangeDetectorRef
    ) {
        this.id = `cvsGrid${++nextUniqueId}`;
    }

    @Input() public set columns(dValue: TColDef[]) {
        const value = dValue ?? [];
        this.storageName = md5(
            JSON.stringify(
                value.map((c) => {
                    return this.getColDefinition(c);
                })
            )
        ).toString();
        const storageColumns = this.gridService.getColumns(this.storageName) as string[];
        this.advSearchFormGroup.addControl('matchAll', new FormControl(false));
        let lastDisplayOrder = 0;
        this._columns = value.map((c) => {
            const numberFormat: {
                align?: CvsColumnAlign;
                headerAlign?: CvsColumnAlign;
            } = {};
            if ((c.type & CvsDataType.number) === CvsDataType.number) {
                numberFormat.align = CvsColumnAlign.right;
                numberFormat.headerAlign = CvsColumnAlign.right;
            }
            c = { ...DEFAULT_GRID_OPTIONS.defaultColumn, ...this.options.defaultColumn, ...numberFormat, ...c };
            c.def = this.getColDefinition(c);
            c.colId = camelize(c.def);
            if (c.isKey && c.def) {
                this.trackByKeyName = c.def;
            }
            if (!c.header) {
                c.header = c.def;
            }

            c.searchable = {
                enable: true,
                label: c.header,
                paramName: c.colId,
                searchFields: [c.colId],
                controlType:
                    c.type === CvsDataType.number
                        ? 'number'
                        : c.type === CvsDataType.boolean
                          ? 'check-box'
                          : (c.searchable?.controlType ?? 'text'),
                caseInsensitive:
                    c.type === CvsDataType.string || c.type === CvsDataType.unknown
                        ? (c.searchable?.caseInsensitive ?? true)
                        : false,
                operator:
                    c.type === CvsDataType.number || c.type === CvsDataType.boolean
                        ? GridifyConditionEnum.Equal
                        : (c.searchable?.operator ?? GridifyConditionEnum.Contains),
                ...(c.searchable ?? {})
            };
            c.columnOrder = c.columnOrder ?? lastDisplayOrder + 1;
            lastDisplayOrder = c.columnOrder;
            c._columnOrder = c.columnOrder;
            c.showInGrid =
                storageColumns.length && storageColumns.length > 0 ? storageColumns.includes(c.colId) : !c.initialHide;
            // NO MATTER WHAT STICK MEANS ALWAYS SHOW
            if (c.sticky == 'end' || c.sticky == 'start') {
                c.initialHide = false;
                c.canShowHide = false;
                // c.exportable = false;
                c.showInGrid = true;
            }
            // NO MATTER WHAT IGNORE MEANS IGNORE
            if ((c.view & CvsCellView.ignore) === CvsCellView.ignore) {
                c.initialHide = true;
                c.canShowHide = false;
                c.showInGrid = false;
                c.exportable = false;
                c.isIgnored = true;
            }
            if ((c.view & CvsCellView.action) === CvsCellView.action) {
                c.canShowHide = false;
                c.exportable = false;
                c.isIgnored = true;
                c.searchable = undefined;
                c.useInCommonSearch = false;
            }
            if (!c.hideHeader) {
                c.hideHeader = 'none';
            }
            if (c.searchable?.enable ?? true) {
                if (c.searchable?.controlType === 'date-range')
                    this.advSearchFormGroup.addControl(
                        c.colId,
                        new FormGroup({
                            start: new FormControl<Date | null>(null),
                            end: new FormControl<Date | null>(null)
                        })
                    );
                else this.advSearchFormGroup.addControl(c.colId, new FormControl(null));
            }
            return c;
        });

        this.enableSorting = this._columns.some(
            (x) => x.sorting && (x.sorting === true || (x.sorting.enabled && x.sorting.enabled === true))
        );
        this.searchInputPlaceHolderText =
            'Search: ' +
            this.columns
                .filter((c) => c.searchable && c.useInCommonSearch)
                .map((c) => this.translator.translate(c.header.toString()))
                .join(', ');
        this.updateDisplayColumns();
    }

    @Input() public set options(value: CvsGridOptions) {
        this._options = deepMerge(DEFAULT_GRID_OPTIONS, value);
        if (this._options.toolbar?.enable === false) {
            this._options.toolbar.showColumnSelector = false;
            this._options.toolbar.showDisplaySelector = false;
            this._options.toolbar.showDownloadOption = false;
            this._options.toolbar.showFullScreenSelector = false;
            this._options.toolbar.showCommonSearch = false;
            this._options.toolbar.showSortSelector = false;
            this._options.toolbar.showAdvancedSearch = false;
        }
        if (
            this._options.toolbar.showColumnSelector ||
            this._options.toolbar.showDisplaySelector ||
            this._options.toolbar.showDownloadOption ||
            this._options.toolbar.showFullScreenSelector ||
            this._options.toolbar.showCommonSearch ||
            this._options.toolbar.showSortSelector ||
            this._options.toolbar.showAdvancedSearch
        ) {
            this._options.toolbar.enable = true;
        }
        if (this._options.defaultSort) {
            this.setCvsGridEvent({ type: 'sort', sort: this._options.defaultSort });
        }
        if (this._options.rowSelectable) {
            this.rowSelection = new SelectionModel<any>(this._options.multiSelectable, this.rowSelected);
        }
        if (this._options.cardColumnMaxSize) {
            const gridCols = [];
            // grid-cols-2 md:grid-cols-4 xl:grid-cols-8
            if (this._options.cardColumnMaxSize.default) {
                gridCols.push(`grid-cols-${this._options.cardColumnMaxSize.default}`);
            }
            if (this._options.cardColumnMaxSize.md) {
                gridCols.push(`md:grid-cols-${this._options.cardColumnMaxSize.md}`);
            }
            if (this._options.cardColumnMaxSize.xl) {
                gridCols.push(`xl:grid-cols-${this._options.cardColumnMaxSize.xl}`);
            }
            this.cardGridCols = gridCols.join(' ');
        }
    }

    // public get advSearchFormGroup() {
    //     const fg = new FormGroup({});
    //     fg.addControl('matchAll', new FormControl(false));
    //     this.columns
    //         .filter((f) => f.searchable?.enable ?? true)
    //         .forEach((column) => {
    //             if (column.searchable?.controlType === 'date-range')
    //                 fg.addControl(
    //                     column.colId,
    //                     new FormGroup({
    //                         start: new FormControl<Date | null>(null),
    //                         end: new FormControl<Date | null>(null)
    //                     })
    //                 );
    //             else fg.addControl(column.colId, new FormControl(null));
    //         });
    //     return fg;
    // }
    public get cellView() {
        return CvsCellView;
    }

    public get columns(): TColDef[] {
        return this._columns.sort((a, b) => a._columnOrder - b._columnOrder);
    }

    public get loadingGridData() {
        return LOADING_GRID_DATA;
    }

    public get options(): CvsGridOptions {
        return this._options;
    }

    public get showAdvancedSearchPanel(): boolean {
        return this._showAdvancedSearchPanel;
    }

    public set showAdvancedSearchPanel(value) {
        this._showAdvancedSearchPanel = value;
        if (!value) {
            this.advanceSearchTempFilters = [];
        }
    }

    public get sortedColumns(): TColDef[] {
        return this._columns.sort((a, b) => a._columnOrder - b._columnOrder);
    }

    public ngOnInit() {
        this.savedSearchItems = this.searchInputCtrl.valueChanges.pipe(
            startWith(''),
            map((value) => this._filterSearch(value || ''))
        );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------
    public ngAfterViewInit(): void {
        if (!this._initialized) {
            const initState: CvsGridEvent = { type: 'init' };
            this.setCvsGridEvent(initState, true);
        }
        this._initialized = true;
    }

    public _getIndex(index: number, dataIndex: number) {
        return typeof index === 'undefined' ? dataIndex : index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ table row selection events
    // -----------------------------------------------------------------------------------------------------
    public _isAllSelected(dataSrc: any[]) {
        const numSelected = this.rowSelection.selected.length;
        const numRows = dataSrc.length;
        return numSelected === numRows;
    }

    public _selectRow(event: MouseEvent, rowData: Record<string, any>, index: number) {
        if (this.options.rowSelectable) {
            // metaKey -> command key
            if (!this.options.multiSelectionWithClick && !event.ctrlKey && !event.metaKey) {
                this.rowSelection.clear();
            }

            this._toggleNormalCheckbox(rowData);
        }

        this.rowClick.emit({ rowData, index });
    }

    /** Select all rows if they are not all selected; otherwise clear selection. */
    public _toggleMasterCheckbox(dataSrc: any[]) {
        this._isAllSelected(dataSrc)
            ? this.rowSelection.clear()
            : dataSrc.forEach((row, _index) => {
                  this.rowSelection.select(row);
              });
        this.rowSelectionChange.emit(this.rowSelection.selected);
    }

    /** Select normal row */
    public _toggleNormalCheckbox(row: Record<string, any>) {
        this.rowSelection.toggle(row);
        this.rowSelectionChange.emit(this.rowSelection.selected);
    }

    public advancedSearchFilter(item: any): boolean {
        return item && item.searchable && !item.isIgnored;
    }

    public allColumnsVisible(): boolean {
        return this.columns.every((column) => column.showInGrid);
    }

    public announceActionItemClick(
        event: MouseEvent,
        colAction: CvsAction,
        cellData: { col: any; element: any; index: number }
    ) {
        event.stopPropagation();
        const { element, index } = cellData;
        const url = colAction.routerLink ? colAction.routerLink(element, index) : null;
        // this.setCvsGridEvent({ type: 'action', action: { ...colAction, item: element } }, true);
        if (url) {
            event.preventDefault();
            this.router.navigateByUrl(url.toString());
            return;
        }
        if (colAction.onClick && typeof colAction.onClick === 'function') {
            const thisObj = colAction.params?.thisObj ?? {};
            colAction.onClick.bind(thisObj, cellData.element, colAction.params, cellData.index)();
        }
    }

    public announceFilterAnyClick(data: string, key: string) {
        if (key !== 'Enter' && key !== 'Manual') {
            return;
        }
        if (key === 'Enter' && data.trim().length <= 0) {
            return;
        }
        const search = new CvsGridSearchEvent();
        const filters: FiltersType = [];
        this.columns.forEach((c) => {
            if (c.searchable && c.useInCommonSearch) {
                const searchParams = c.searchable?.paramName?.split(',');
                if (c.type === CvsDataType.date && c.searchable.operator === GridifyConditionEnum.Contains) {
                    searchParams.forEach((item) => {
                        filters.push({
                            key: item,
                            value: (data + '').trim(),
                            caseInsensitive: false,
                            operator: GridifyConditionEnum.GreaterThanOrEqual
                        });
                        filters.push(GridifyLogicalEnum.Or);
                    });
                } else {
                    searchParams.forEach((item) => {
                        filters.push({
                            key: item,
                            value: (data + '').trim(),
                            caseInsensitive: c.searchable.caseInsensitive,
                            operator: c.searchable.operator
                        });
                        filters.push(GridifyLogicalEnum.Or);
                    });
                }
            }
        });
        filters.pop();
        search.filters = [...filters];
        this.setCvsGridEvent(
            {
                type: 'search',
                search
            },
            true
        );
    }

    public announcePageChange(pageState: PageEvent) {
        pageState.pageIndex++;
        pageState.previousPageIndex = this.previousPageIndex;
        //this.pageChange.emit(pageState as CvsGridPaginatedEvent);
        this.setCvsGridEvent({ type: 'page', page: pageState }, true);
        this.previousPageIndex = pageState.pageIndex;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ table events
    // -----------------------------------------------------------------------------------------------------
    public announceSortChange(sortState: Sort) {
        //this.sortChange.emit(sortState);
        this.setCvsGridEvent({ type: 'sort', sort: { field: sortState.active, direction: sortState.direction } }, true);
    }

    public applyColumnSelection(): void {
        this.showColumnSelection = !this.showColumnSelection;
    }

    public columnSelectionFilter(item: any): boolean {
        return item && item.canShowHide;
    }

    public displaySearchInputFn(gridSearchDataType: CvsGridSearchDataType): string {
        return gridSearchDataType && gridSearchDataType.name ? gridSearchDataType.name : '';
    }

    public drop({ currentIndex, previousIndex }: CdkDragDrop<CvsGridColumn<any>[]>) {
        const displayColumnsClone = [...this.columns.sort((a, b) => (a._columnOrder ?? 0) - (b._columnOrder ?? 0))];
        moveItemInArray(displayColumnsClone, previousIndex, currentIndex);
        for (let index = 0; index < displayColumnsClone.length; index++) {
            displayColumnsClone[index]._columnOrder = index + 1;
        }
        this.updateDisplayColumns(displayColumnsClone);
    }

    public getActionItemData(colAction: CvsAction, colDef: CvsGridColumn<any>, element: any, index?: number) {
        const cssClass = colDef.dataCssClassFormatter ? colDef.dataCssClassFormatter(element, index) : '';
        const url = colAction.routerLink ? colAction.routerLink(element, index) : null;
        return {
            cssClass,
            href: `${url ?? '#'}`
        };
    }

    /***
     * Get Css Class for TD or TH
     */
    public getColumnCssClass(colDef: CvsGridColumn<any>, cellTag: 'td' | 'th' | 'card' = 'td'): string {
        const colInfo: CvsGridColumn<any> = {
            align: CvsColumnAlign.left,
            headerAlign: CvsColumnAlign.auto,
            view: CvsCellView.text,
            ...colDef
        };
        const colDefViews = this.Enum2Values(colInfo.view);
        const cssClassList: string[] = [];
        cssClassList.push(
            ...colDefViews.map(
                (c) => `cvs-grid${cellTag == 'card' ? '-card' : ''}${cellTag == 'th' ? '-header' : ''}-${c}`
            )
        );

        if (cellTag == 'th') {
            cssClassList.push(`cvs-grid-header-${CvsColumnAlign[colInfo.headerAlign]}`);
            if (colInfo.headerAlign == CvsColumnAlign.auto) {
                cssClassList.push(`cvs-grid-${CvsColumnAlign[colInfo.align]}__header-auto`);
            }
        } else {
            cssClassList.push(`cvs-grid-${CvsColumnAlign[colInfo.align]}`);
        }
        if (cellTag == 'card') {
            if (colDef.colSpan) {
                cssClassList.push(`col-span-${colDef.colSpan}`);
            }
        }
        if (colInfo.cssClasses) cssClassList.push(colInfo.cssClasses);
        return cssClassList.join(' ');
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public getColumnHeaderData(colDef: CvsGridColumn<any>, _index?: number): string {
        if (colDef.header) {
            if (typeof colDef.header === 'string') {
                return colDef.header;
            }
        }
        return colDef.field.toString();
    }

    public getColumnObject(
        colDef: CvsGridColumn<any>,
        element: any,
        index?: number
    ): {
        cellData?: string;
        chipColor?: string;
        cssClass?: string;
        url?: string;
        view?: string;
        tooltip?: string;
    } {
        const result: {
            cellData?: string;
            chipColor?: string;
            cssClass?: string;
            url?: string;
            view?: string;
            tooltip?: string;
        } = {
            cellData: typeof colDef.field
        };
        const colDefView: CvsCellView = colDef.view || CvsCellView.text;

        if (element) {
            result.cellData =
                typeof colDef.field === 'string' || typeof colDef.field === 'number' || typeof colDef.field === 'symbol'
                    ? element[colDef.field.toString()]
                    : colDef.field(element);
            if ((colDefView & CvsCellView.template) === CvsCellView.template) {
                result.view = 'template';
                return result;
            }
            const dateFormat: string[] = [];
            if (CvsDataType.date === (colDef.type & CvsDataType.date)) {
                dateFormat.push('dd-MMM-yy');
            }
            if (CvsDataType.timeAmPm === (colDef.type & CvsDataType.timeAmPm)) {
                dateFormat.push('hh:mm:ss a');
            }
            if (CvsDataType.timeHours === (colDef.type & CvsDataType.timeHours)) {
                dateFormat.push('HH:mm');
            }
            if (CvsDataType.zone === (colDef.type & CvsDataType.zone)) {
                dateFormat.push('zzzz');
            }
            if (dateFormat.length > 0) {
                result.cellData = new DatePipe('en').transform(result.cellData, dateFormat.join(' '));
            }
            if (colDef.formatter) {
                result.cellData = colDef.formatter(result.cellData, element, index);
            }

            if ((colDefView & CvsCellView.chip) === CvsCellView.chip) {
                result.view = 'chip';
                result.chipColor = colDef.dataCssClassFormatter
                    ? colDef.dataCssClassFormatter(element, index)
                    : 'bg-yellow-100 text-yellow-800';
            } else if ((colDefView & CvsCellView.link) === CvsCellView.link) {
                result.view = 'link';
                result.cssClass = colDef.dataCssClassFormatter ? colDef.dataCssClassFormatter(element, index) : '';
                result.url = colDef.routerLink ? colDef.routerLink(element, index) : null;
            } else if ((colDefView & CvsCellView.html) === CvsCellView.html) {
                result.view = 'html';
                result.cssClass = colDef.dataCssClassFormatter ? colDef.dataCssClassFormatter(element, index) : '';
            } else if (
                (colDefView & CvsCellView.text) === CvsCellView.text ||
                (colDefView & CvsCellView.bold) === CvsCellView.bold ||
                (colDefView & CvsCellView.italic) === CvsCellView.italic
            ) {
                result.view = 'text';
                result.cssClass = colDef.dataCssClassFormatter ? colDef.dataCssClassFormatter(element, index) : '';
            }
            result.tooltip = undefined;
            if (colDef.tooltip === true) {
                result.tooltip = result.cellData;
            } else if (typeof colDef.tooltip === 'function') {
                result.tooltip = colDef.tooltip(result.cellData, element, index);
            }
        }
        return result;
    }

    public getDataPager(gridData: any | null | undefined): GridPagination | null | undefined {
        if (gridData && gridData.metadata && gridData.metadata.pagination) {
            const pagination = gridData.metadata.pagination as GridPagination;
            this.setCvsGridEvent({
                type: 'page',
                page: {
                    pageIndex: pagination.page,
                    length: pagination.totalItemCount,
                    pageSize: pagination.pageSize
                }
            });
            return gridData.metadata.pagination;
        }
        return undefined;
    }

    public getMessageBody(
        gridData: any | null | undefined,
        prop: 'color' | 'icon' | 'body' | 'title' = 'body'
    ): string | null | undefined {
        if (gridData && gridData.metadata && gridData.metadata.message && gridData.metadata.message[prop]) {
            return gridData.metadata.message[prop];
        }
        return null;
    }

    public inverseDisplayColumns() {
        this.columns.forEach((c) => {
            if (c.canShowHide) {
                c.showInGrid = !c.showInGrid;
            }
        });
    }

    public onAddNewFilter(col: TColDef, operator: string, value: string) {
        this.advanceSearchTempFilters.push({
            pk: Guid.create().toString(),
            key: col.searchable?.paramName,
            label: this.getColumnHeaderData(col),
            value,
            operator: this.stringToEnumValue(GridifyConditionEnum, operator),
            operatorLabel: this.getEnumKeyByEnumValue(GridifyConditionEnum, operator)
        });
    }

    public onAdvancedSearchClick(): void {
        this.showAdvancedSearchPanel = !this.showAdvancedSearchPanel;
    }

    public onApplyAdvanceSearch(_saveName: string = undefined): void {
        const searchArray: FiltersType = [];
        const rawData = this.advSearchFormGroup.getRawValue() as any & { matchAll: boolean };
        const isMatchAll = rawData['matchAll'];
        delete rawData['matchAll'];
        const searchModel = {};
        Object.keys(rawData).forEach((key) => {
            if (rawData[key]) {
                searchModel[key] = rawData[key];
            }
        });

        for (const field in searchModel) {
            const column = this.columns.find((c) => c.field === field);
            if (column?.searchable?.enable && searchModel[field] !== undefined) {
                const searchFieldFilter: FiltersType = [];
                const fieldValues = Array.isArray(searchModel[field]) ? searchModel[field] : [searchModel[field]];
                for (let i = 0; i < fieldValues.length; i++) {
                    const value = fieldValues[i];
                    if (value === undefined || value === null || value === '') {
                        continue;
                    }
                    for (let j = 0; j < (column.searchable.searchFields?.length ?? 0); j++) {
                        if (!column.searchable.searchFields) continue;
                        const searchField = column.searchable.searchFields[j];
                        if (column.searchable.controlType === 'date' && value instanceof DateTime) {
                            const dateValue = value as DateTime;
                            const dateFilter: FiltersType = [];
                            dateFilter.push({
                                key: searchField,
                                value: dateValue.startOf('day').toSQL() + '',
                                operator: GridifyConditionEnum.GreaterThanOrEqual,
                                caseInsensitive: false
                            });
                            dateFilter.push(GridifyLogicalEnum.And);
                            dateFilter.push({
                                key: searchField,
                                value: dateValue.endOf('day').toSQL() + '',
                                operator: GridifyConditionEnum.LessThanOrEqual,
                                caseInsensitive: false
                            });
                            searchFieldFilter.push(dateFilter);
                        } else if (column.searchable.controlType === 'date-range' && value) {
                            const dateRange = value as { start: DateTime | null; end: DateTime | null };
                            if (!dateRange.start && !dateRange.end) continue;
                            const dateFilter: FiltersType = [];
                            if (dateRange.start) {
                                dateFilter.push({
                                    key: searchField,
                                    value: dateRange.start.startOf('day').toSQL() + '',
                                    operator: GridifyConditionEnum.GreaterThanOrEqual,
                                    caseInsensitive: false
                                });
                            }
                            if (dateRange.end) {
                                if (dateFilter.length > 0) {
                                    dateFilter.push(GridifyLogicalEnum.And);
                                }
                                dateFilter.push({
                                    key: searchField,
                                    value: dateRange.end.endOf('day').toSQL() + '',
                                    operator: GridifyConditionEnum.LessThanOrEqual,
                                    caseInsensitive: false
                                });
                            }
                            if (dateFilter.length > 0) {
                                searchFieldFilter.push(dateFilter);
                            }
                        } else {
                            const filterData = {
                                key: searchField,
                                value: this.customEncode(value)
                            };
                            try {
                                const pk = JSON.parse(value) as { key: string; value: any };
                                if (pk.key !== undefined && pk.value !== undefined) {
                                    (filterData.key = pk.key), (filterData.value = pk.value);
                                }
                            } catch (error) {
                                // nothing eat the error
                            }
                            searchFieldFilter.push({
                                operator: column.searchable.operator || GridifyConditionEnum.Contains,
                                caseInsensitive: column.searchable.caseInsensitive ?? true,
                                ...filterData
                            });
                        }
                        searchFieldFilter.push(column.searchable.searchFieldsAndOr || GridifyLogicalEnum.Or);
                    }
                }
                if (searchFieldFilter.length > 0) {
                    searchFieldFilter.pop();
                    searchArray.push(searchFieldFilter.length > 1 ? searchFieldFilter : searchFieldFilter[0]);
                    searchArray.push(isMatchAll ? GridifyLogicalEnum.And : GridifyLogicalEnum.Or);
                }
            }
        }
        if (searchArray.length > 0) {
            searchArray.pop();
        }
        const search = new CvsGridSearchEvent();
        search.filters = [...searchArray];
        this.setCvsGridEvent(
            {
                type: 'search',
                search
            },
            true
        );
    }

    public onApplyAdvanceSearch1(saveName: string = undefined): void {
        const rawData = this.advSearchFormGroup.getRawValue() as any & { matchAll: boolean };
        const _matchAll = rawData['matchAll'];
        delete rawData['matchAll'];
        this.advanceSearchTempFilters = Object.keys(rawData)
            .filter((k) => rawData[k])
            .map((k) => {
                return {
                    key: k,
                    value: rawData[k],
                    caseInsensitive: false
                } as CvsFilterType;
            });
        this.showAdvancedSearchSaveInput = false;
        const search = new CvsGridSearchEvent();
        const filters: FiltersType = [];
        this.advanceSearchTempFilters.forEach((f) => {
            if (f.key) {
                const searchParams = f.key?.split(',');
                searchParams.forEach((item) => {
                    filters.push({
                        key: item,
                        value: (f.value + '').trim(),
                        caseInsensitive: f.caseInsensitive ?? true,
                        operator: f.operator ?? GridifyConditionEnum.Contains
                    });
                    filters.push(_matchAll ? GridifyLogicalEnum.And : GridifyLogicalEnum.Or);
                });
            }
        });
        filters.pop();
        search.filters = [...filters];
        if (saveName && saveName.length > 0) {
            this.gridService.saveSearches(this.storageName, {
                id: new Date().valueOf(),
                name: saveName,
                filters: this.advanceSearchTempFilters
            });
        }
        this.setCvsGridEvent(
            {
                type: 'search',
                search
            },
            true
        );
    }

    public onDownloadClick(file: downloadFileTypes): void {
        if (!this.options.downloadOption.func) {
            alert('Missing "downloadOption.func"');
            return;
        }
        const fileName = `${this.id}_${new DatePipe('en').transform(new Date(), 'yyyyMMddHHmmss')}`.toLowerCase();
        const currentState = this.getCvsGridEvent();
        currentState.page = { pageIndex: 0, pageSize: 32767, length: 0 };
        const dataOf = this.options.downloadOption.func(currentState).pipe(
            map((res) => {
                if (res && res?.items) {
                    const data2Export: any[] = [];
                    if (file !== 'Json') {
                        const headerData: any = {};
                        this.columns.forEach((col, index) => {
                            if (col.exportable) {
                                headerData[col.colId] = this.translator.translate(this.getColumnHeaderData(col, index));
                            }
                        });
                        data2Export.push(headerData);
                    }
                    res?.items.forEach((row, rowIndex) => {
                        const rowData: any = {};
                        this.columns.forEach((col) => {
                            if (col.exportable) {
                                rowData[col.colId] = this.getColumnObject(col, row, rowIndex)?.cellData;
                            }
                        });
                        data2Export.push(rowData);
                    });
                    return data2Export;
                } else {
                    alert('Missing "Data/onDownloadClick"');
                    return [];
                }
            })
        );
        switch (file) {
            case 'Csv':
                this.onExport2CsvClick(fileName, dataOf);
                return;
            case 'Json':
                this.onExport2JsonClick(fileName, dataOf);
                return;
            default:
                this.onExport2ExcelClick(fileName, dataOf);
                break;
        }
    }

    public onExport2CsvClick(fileName: string, dataOf: Observable<any[]>): void {
        dataOf.subscribe((data2Export) => {
            if (data2Export.length > 0) {
                this.excelService.exportJsonAsCsvFile(fileName, data2Export);
            }
        });
    }

    public onExport2ExcelClick(fileName: string, dataOf: Observable<any[]>): void {
        dataOf.subscribe((data2Export) => {
            if (data2Export.length > 0) {
                this.excelService.exportJsonAsExcelFile(fileName, this.id, data2Export);
            }
        });
    }

    public onExport2JsonClick(fileName: string, dataOf: Observable<any[]>): void {
        dataOf.subscribe((data2Export) => {
            if (data2Export.length > 0) {
                this.excelService.exportJsonAsJsonFile(fileName, data2Export);
            }
        });
    }

    public onGridModeClick(gridMode: any): void {
        this._options.mode = gridMode;
    }

    public onRemoveFilter(pk: string) {
        this.advanceSearchTempFilters = this.advanceSearchTempFilters.filter((f) => f.pk != pk);
    }

    public onSearchOptionSelected(e: MatAutocompleteSelectedEvent) {
        const selectedValue = e.option.value as CvsGridSearchDataType;
        this.advanceSearchTempFilters = selectedValue.filters;
        this.onApplyAdvanceSearch(undefined);
    }

    public onShowHideColumnCheckboxChange(item: CvsGridColumn<TData>) {
        item.showInGrid = !item.showInGrid;
        this.updateDisplayColumns();
    }

    public onToggleAllColumns() {
        const allColumnsVisible = this.allColumnsVisible();
        this.selectAllDisplayColumns(!allColumnsVisible);
    }

    public onViewColumnClick(): void {
        this.showColumnSelection = !this.showColumnSelection;
        this.cdr.detectChanges();
    }

    public selectAllDisplayColumns(flag?: boolean) {
        if (nil<boolean>(flag)) {
            this.columns.forEach((c) => {
                if (c.canShowHide) {
                    c.showInGrid = !c.initialHide;
                    c._columnOrder = c.columnOrder;
                }
            });
        } else {
            this.columns.forEach((c) => {
                if (c.canShowHide) {
                    c.showInGrid = flag;
                }
            });
        }
        this.updateDisplayColumns();
    }

    public trackByColumn(index: number, item: any): any {
        return item.colId || item.key || index;
    }

    public trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    public trackByMatTableFn(index: number, item: any): any {
        return item[this.trackByKeyName] || item.id || index;
    }

    public updateDisplayColumns(currentColumnValues?: CvsGridColumn<TData>[]): void {
        currentColumnValues = currentColumnValues || this.columns;
        currentColumnValues.forEach((x) => {
            if (x.showInGrid && x.actions === undefined) {
                x.exportable = true;
            } else {
                x.exportable = false;
            }
        });

        this.displayedColumns = currentColumnValues
            .filter((f) => f.showInGrid)
            .sort((n1, n2) => n1._columnOrder - n2._columnOrder)
            .map((colDef) => colDef.def);

        if (this._options.cardColumnMaxSize) {
            const colLength = currentColumnValues.filter((f) => f.showInGrid && f.view !== CvsCellView.action).length;
            const gridCols = [];
            // grid-cols-2 md:grid-cols-4 xl:grid-cols-8
            if (this._options.cardColumnMaxSize.default) {
                gridCols.push(`grid-cols-${Math.min(colLength, this._options.cardColumnMaxSize.default)}`);
            }
            if (this._options.cardColumnMaxSize.md) {
                gridCols.push(`md:grid-cols-${Math.min(colLength, this._options.cardColumnMaxSize.md)}`);
            }
            if (this._options.cardColumnMaxSize.xl) {
                gridCols.push(`xl:grid-cols-${Math.min(colLength, this._options.cardColumnMaxSize.xl)}`);
            }
            this.cardGridCols = gridCols.join(' ');
        }
        if (this._options?.rowSelectable && this._options?.mode === 'table') {
            this.displayedColumns.unshift('CvsGridCheckboxColumnDef');
        }
        if (this._options?.toolbar?.showColumnSelector) {
            this.gridService.saveColumn(this.storageName, this.displayedColumns);
        }
    }

    private Enum2Values(enumView: CvsCellView) {
        const values: string[] = [];

        while (enumView) {
            const bit = enumView & (~enumView + 1);
            values.push(CvsCellView[bit]);
            enumView ^= bit;
        }
        return values;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private Functions
    // -----------------------------------------------------------------------------------------------------
    private _filterSearch(value: string): CvsGridSearchDataType[] {
        const filterValue = (value + '').toLowerCase();
        return this.gridService
            .getSearch(this.storageName)
            .filter((option) => option.name.toLowerCase().includes(filterValue));
    }

    private customEncode(value: string): string {
        if (!value) return value;

        return value.toString().replace(/[|,*/]/g, (match) => {
            switch (match) {
                case '|':
                    return '%7C';
                case ',':
                    return '%2C';
                case '*':
                    return '%2A';
                case '/':
                    return '%2F';
                default:
                    return match;
            }
        });
    }

    private getColDefinition(colDef: CvsGridColumn<TData>) {
        return typeof colDef.field === 'string' || typeof colDef.field === 'number' || typeof colDef.field === 'symbol'
            ? colDef.field.toString()
            : colDef.key?.toString();
    }

    private getCvsGridEvent(): CvsGridEvent {
        return this.store.state;
    }

    private getEnumKeyByEnumValue<TEnumKey extends string, TEnumVal extends string | number>(
        myEnum: { [key in TEnumKey]: TEnumVal },
        enumValue: TEnumVal
    ): string {
        const keys = (Object.keys(myEnum) as TEnumKey[]).filter((x) => myEnum[x] === enumValue);
        return keys.length > 0 ? keys[0] : '';
    }

    private setCvsGridEvent(event: Partial<CvsGridEvent>, fireEvent = false): void {
        const currentState = this.getCvsGridEvent();
        const newState = { ...currentState, ...event };
        this.store.set(newState);
        if (fireEvent) {
            this.gridEvents.emit(newState);
        }
    }

    private stringToEnumValue = <T, K extends keyof T>(enumObj: T, value: string): T[keyof T] | undefined =>
        enumObj[Object.keys(enumObj).filter((k) => enumObj[k as K].toString() === value)[0] as keyof typeof enumObj];
}
